import { isEmpty } from 'lodash';

import { createUrl, restGet, restPatch, restPost } from '~/src/dash/api/rest';

export interface ISmartFloorConfig {
  Active: boolean;
  ControlTrafficPercentage: number;
  CreatedAt: string;
  DeletedAt: string | null;
  HeaderbidderID: number;
  ID: number;
  IsABTestEnabled: boolean;
  IsMaxBidFloorPercentage: boolean;
  IsMinBidFloorPercentage: boolean;
  IsTestIncrementHighPercentage: boolean;
  IsTestIncrementLowPercentage: boolean;
  MaxBidFloor: number;
  MinBidFloor: number;
  SmartFloors: ISmartFloorPayloadUI[];
  TestIncrementHigh: number;
  TestIncrementLow: number;
  ThompsonSamplingEnabled: boolean;
  UpdatedAt: string | null;
}

export interface InventorySmartFloorConfig {
  Active: boolean;
  ControlTrafficPercentage: number;
  CreatedAt: string;
  DeletedAt: string | null;
  SiteID: number;
  ID: number;
  IsABTestEnabled: boolean;
  IsTestIncrementPercentage: boolean;
  IsBidFloorPercentage: boolean;
  MaxBidFloor: number;
  MinBidFloor: number;
  InventorySmartFloors: IInventorySmartFloorPayloadUI[];
  TestIncrement: number;
  UpdatedAt: string | null;
  FallBackBaseFloor: number;
  ExcludedHeaderbidderIDs: string;
}

export interface ISmartFloorConfigUI extends ISmartFloorConfig {
  shouldAllowSortingTable?: boolean;
}

export interface IInventorySmartFloorConfigUI extends InventorySmartFloorConfig {
  shouldAllowSortingTable?: boolean;
}

export interface ISmartFloorPayload {
  Active: boolean;
  AssociatedChannelGroupIDs: Number[];
  BaseBidFloor: number;
  EstimatedRevenueUplift: number;
  EstimatedRevenueUplift12HourTrend?: number | null;
  HeaderbidderID: number;
  ID: number;
  IsTargetedThroughChannelAssociation: boolean;
  LastRestartTime: string | null;
  MaxBidFloor: number;
  MinBidFloor: number;
  OptimizedBidFloor: number;
  OptimizedFloor12HourTrend?: number | null;
  RestartVersion: number;
  SiteID: number;
  Status: string;
  TestIncrementHigh: number;
  TestIncrementLow: number;
  TestPeriod: number;
  ThompsonSamplingEnabled?: boolean;
}

// inventory smart floor
export interface IInventorySmartFloorPayload {
  Active: boolean;
  ControlBidFloor: number;
  EstimatedRevenueUplift: number;
  EstimatedRevenueUplift12HourTrend?: number | null;
  SiteID: number;
  HeaderbidderID: number;
  PublisherID: number;
  ID: number;
  MaxBidFloor: number;
  MinBidFloor: number;
  RestartVersion: number;
  Status: string;
  TestIncrement: number;
  TestPeriod: number;
  FallBackBaseFloor: number;
}

export interface ISmartFloorPayloadUI extends ISmartFloorPayload {
  // For the ListView
  ChannelName?: string;
  ChannelGroupNames?: string[];
}

export interface IInventorySmartFloorPayloadUI extends IInventorySmartFloorPayload {
  // For the ListView
  BidderName?: string;
  BidderID?: number;
  isUsingFallbackFloor?: boolean;
}

enum SmartFloorStatus {
  Unknown = 0,
  Pending = 1,
  Running = 2,
}

const SmartFloorStatusTextMap: Record<SmartFloorStatus, string> = {
  [SmartFloorStatus.Unknown]: 'Unknown',
  [SmartFloorStatus.Pending]: 'Pending',
  [SmartFloorStatus.Running]: 'Running',
};

export type smartFloorConfigCols =
  | 'BaseBidFloor'
  | 'IsABTestEnabled'
  | 'IsMaxBidFloorPercentage'
  | 'IsMinBidFloorPercentage'
  | 'isSiteIDSelected'
  | 'IsTestIncrementHighPercentage'
  | 'IsTestIncrementLowPercentage'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'SmartFloors'
  | 'TestIncrementHigh'
  | 'TestIncrementLow'
  | 'ThompsonSamplingEnabled';

export type inventorySmartFloorConfigCols =
  | 'ControlBidFloor'
  | 'IsABTestEnabled'
  | 'IsTestIncrementPercentage'
  | 'IsBidFloorPercentage'
  | 'TestIncrement'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'InventorySmartFloors'
  | 'FallBackBaseFloor'
  | 'ExcludedHeaderbidderIDs'
  | 'isBidderIDSelected';

export const smartFloorRecalcKeys = new Set([
  'SmartFloors',
  'BaseBidFloor',
  'IsTestIncrementHighPercentage',
  'IsTestIncrementLowPercentage',
  'TestIncrementHigh',
  'TestIncrementLow',
  'IsMaxBidFloorPercentage',
  'IsMinBidFloorPercentage',
  'MaxBidFloor',
  'MinBidFloor',
]);

/* eslint-disable i18next/no-literal-string */
export const inventorySmartFloorRecalcKeys = new Set([
  'ControlBidFloor',
  'IsTestIncrementPercentage',
  'IsBidFloorPercentage',
  'MaxBidFloor',
  'MinBidFloor',
  'InventorySmartFloors',
  'TestIncrement',
  'FallBackBaseFloor',
  'ExcludedHeaderbidderIDs',
]);
/* eslint-enable i18next/no-literal-string */

export type SmartFloorsAPIKey =
  | 'Active'
  | 'AssociatedChannelGroupIDs'
  | 'BaseBidFloor'
  | 'CreatedAt'
  | 'DeletedAt'
  | 'EstimatedRevenueUplift'
  | 'EstimatedRevenueUplift12HourTrend' // TODO need these values from the cronjob
  | 'HeaderbidderID'
  | 'ID'
  | 'IsTargetedThroughChannelAssociation'
  | 'isSiteIDSelected'
  | 'LastRestartTime'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'OptimizedBidFloor'
  | 'OptimizedFloor12HourTrend'
  | 'RestartVersion'
  | 'SiteID'
  | 'Status'
  | 'TestIncrementHigh'
  | 'TestIncrementLow'
  | 'TestPeriod'
  | 'UpdatedAt';

export type InventorySmartFloorsAPIKey =
  | 'Active'
  | 'ControlBidFloor'
  | 'CreatedAt'
  | 'DeletedAt'
  | 'EstimatedRevenueUplift'
  | 'EstimatedRevenueUplift12HourTrend' // TODO need these values from the cronjob
  | 'HeaderbidderID'
  | 'ID'
  | 'isBidderIDSelected'
  | 'LastRestartTime'
  | 'MaxBidFloor'
  | 'MinBidFloor'
  | 'RestartVersion'
  | 'SiteID'
  | 'Status'
  | 'TestIncrement'
  | 'TestPeriod'
  | 'UpdatedAt'
  | 'FallBackBaseFloor'
  | 'ExcludedHeaderbidderIDs';
/** genNewSmartFloorConfig
 * innitialize an empty config when smart floor is turned on for the first time
 */
export function genNewSmartFloorConfig(headerbidderID: number): ISmartFloorConfigUI {
  const smartFloorConfig: ISmartFloorConfigUI = {
    Active: true,
    ControlTrafficPercentage: 0,
    CreatedAt: '',
    DeletedAt: null,
    HeaderbidderID: headerbidderID,
    ID: 0,
    IsABTestEnabled: false,
    IsMaxBidFloorPercentage: false,
    IsMinBidFloorPercentage: false,
    IsTestIncrementHighPercentage: false,
    IsTestIncrementLowPercentage: false,
    MaxBidFloor: 0,
    MinBidFloor: 0,
    SmartFloors: [] as ISmartFloorPayloadUI[],
    TestIncrementHigh: 0,
    TestIncrementLow: 0,
    ThompsonSamplingEnabled: false,
    UpdatedAt: null,
  };
  return smartFloorConfig;
}

export function genNewInventorySmartFloorConfig(siteID: number): IInventorySmartFloorConfigUI {
  const inventorySmartFloorConfig: IInventorySmartFloorConfigUI = {
    Active: true,
    ControlTrafficPercentage: 0,
    CreatedAt: '',
    DeletedAt: null,
    SiteID: siteID,
    ID: 0,
    IsABTestEnabled: false,
    IsTestIncrementPercentage: false,
    IsBidFloorPercentage: false,
    MaxBidFloor: 0,
    MinBidFloor: 0,
    InventorySmartFloors: [] as IInventorySmartFloorPayloadUI[],
    TestIncrement: 0.05,
    UpdatedAt: null,
    FallBackBaseFloor: 0,
    ExcludedHeaderbidderIDs: '',
  };
  return inventorySmartFloorConfig;
}

export function convertNumToFixedDecimalNum(num: number, fractionDigits: number): number {
  return Number(num.toFixed(fractionDigits));
}

interface ISmartFloorParams {
  MaxBidFloor: number;
  MinBidFloor: number;
  TestIncrementHigh: number;
  TestIncrementLow: number;
}

interface IInventorySmartFloorParams {
  FallBackBaseFloor: number;
  MaxBidFloor: number;
  MinBidFloor: number;
  TestIncrement: number;
  ExcludedHeaderbidderIDs: string;
}

export function computeSmartFloorParamsFromConfig(
  smartFloorConfig: ISmartFloorConfigUI,
  baseFloor: number,
): ISmartFloorParams {
  const paramsFromConfig = {} as ISmartFloorParams;

  paramsFromConfig.TestIncrementHigh = convertNumToFixedDecimalNum(
    smartFloorConfig.TestIncrementHigh,
    2,
  );
  paramsFromConfig.TestIncrementLow = convertNumToFixedDecimalNum(
    smartFloorConfig.TestIncrementLow,
    2,
  );

  if (
    smartFloorConfig.IsTestIncrementHighPercentage ||
    smartFloorConfig.IsTestIncrementLowPercentage
  ) {
    paramsFromConfig.TestIncrementHigh = convertNumToFixedDecimalNum(
      (smartFloorConfig.TestIncrementHigh * baseFloor) / 100,
      2,
    );
    paramsFromConfig.TestIncrementLow = convertNumToFixedDecimalNum(
      (smartFloorConfig.TestIncrementLow * baseFloor) / 100,
      2,
    );
  }

  paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(smartFloorConfig.MaxBidFloor, 2);
  paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(smartFloorConfig.MinBidFloor, 2);

  if (smartFloorConfig.IsMaxBidFloorPercentage || smartFloorConfig.IsMinBidFloorPercentage) {
    paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(
      baseFloor + (smartFloorConfig.MaxBidFloor * baseFloor) / 100,
      2,
    );
    paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(
      baseFloor - (smartFloorConfig.MinBidFloor * baseFloor) / 100,
      2,
    );
  }

  return paramsFromConfig;
}

export function computeInventorySmartFloorParamsFromConfig(
  inventorySmartFloorConfig: IInventorySmartFloorConfigUI,
  controlBidFloor: number,
): IInventorySmartFloorParams {
  const paramsFromConfig = {} as IInventorySmartFloorParams;

  paramsFromConfig.FallBackBaseFloor = convertNumToFixedDecimalNum(
    inventorySmartFloorConfig.FallBackBaseFloor,
    2,
  );
  paramsFromConfig.TestIncrement = convertNumToFixedDecimalNum(
    inventorySmartFloorConfig.TestIncrement,
    2,
  );

  if (inventorySmartFloorConfig.IsTestIncrementPercentage) {
    paramsFromConfig.TestIncrement = convertNumToFixedDecimalNum(
      (inventorySmartFloorConfig.TestIncrement * controlBidFloor) / 100,
      2,
    );
  }

  paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(
    inventorySmartFloorConfig.MaxBidFloor,
    2,
  );
  paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(
    inventorySmartFloorConfig.MinBidFloor,
    2,
  );

  if (inventorySmartFloorConfig.IsBidFloorPercentage) {
    paramsFromConfig.MaxBidFloor = convertNumToFixedDecimalNum(
      controlBidFloor + (inventorySmartFloorConfig.MaxBidFloor * controlBidFloor) / 100,
      2,
    );
    paramsFromConfig.MinBidFloor = convertNumToFixedDecimalNum(
      controlBidFloor - (inventorySmartFloorConfig.MinBidFloor * controlBidFloor) / 100,
      2,
    );
  }

  return paramsFromConfig;
}

export function genNewSmartFloors(
  siteID: number,
  headerbidderID: number,
  baseFloor: number,
  smartFloorConfig: ISmartFloorConfigUI,
  channelGroupIDs: number[],
  isFromChannelSelect: boolean,
): ISmartFloorPayloadUI {
  const smartFloorParams = computeSmartFloorParamsFromConfig(smartFloorConfig, baseFloor);

  return {
    Active: true,
    AssociatedChannelGroupIDs: channelGroupIDs,
    BaseBidFloor: convertNumToFixedDecimalNum(baseFloor, 2),
    EstimatedRevenueUplift: 0,
    EstimatedRevenueUplift12HourTrend: 0,
    HeaderbidderID: headerbidderID,
    ID: 0,
    IsTargetedThroughChannelAssociation: isFromChannelSelect,
    LastRestartTime: null,
    MaxBidFloor: convertNumToFixedDecimalNum(smartFloorParams.MaxBidFloor, 2),
    MinBidFloor: convertNumToFixedDecimalNum(smartFloorParams.MinBidFloor, 2),
    OptimizedBidFloor: convertNumToFixedDecimalNum(baseFloor, 2),
    OptimizedFloor12HourTrend: 0,
    RestartVersion: 0,
    SiteID: siteID,
    Status: SmartFloorStatusTextMap[SmartFloorStatus.Pending],
    TestIncrementHigh: convertNumToFixedDecimalNum(smartFloorParams.TestIncrementHigh, 2),
    TestIncrementLow: convertNumToFixedDecimalNum(smartFloorParams.TestIncrementLow, 2),
    TestPeriod: 0,
    ThompsonSamplingEnabled: smartFloorConfig.ThompsonSamplingEnabled,
  } as ISmartFloorPayloadUI;
}

export function updateSmartFloorParamsFromConfig(
  smartFloorConfig: ISmartFloorConfigUI,
  baseFloor: number,
  smartFloor: ISmartFloorPayloadUI,
  selectedSiteIDToSelectedChannelGroupIDs: { [key: number]: Set<number> },
  selectedChannelIDs: Set<number>,
  shouldUpdateSmartFloorParams: boolean,
): ISmartFloorPayloadUI {
  smartFloor.AssociatedChannelGroupIDs = !isEmpty(
    selectedSiteIDToSelectedChannelGroupIDs[smartFloor.SiteID],
  )
    ? Array.from(selectedSiteIDToSelectedChannelGroupIDs[smartFloor.SiteID])
    : [];
  smartFloor.IsTargetedThroughChannelAssociation = selectedChannelIDs.has(smartFloor.SiteID);
  smartFloor.OptimizedBidFloor = baseFloor;
  smartFloor.BaseBidFloor = baseFloor;
  if (shouldUpdateSmartFloorParams) {
    const params = computeSmartFloorParamsFromConfig(smartFloorConfig, baseFloor);
    smartFloor.MaxBidFloor = params.MaxBidFloor;
    smartFloor.MinBidFloor = params.MinBidFloor;
    smartFloor.TestIncrementHigh = params.TestIncrementHigh;
    smartFloor.TestIncrementLow = params.TestIncrementLow;
  }

  return smartFloor;
}

export function updateInventorySmartFloorParamsFromConfig(
  inventorySmartFloorConfig: IInventorySmartFloorConfigUI,
  controlBidFloor: number,
  inventorySmartFloor: IInventorySmartFloorPayloadUI,
  shouldUpdateSmartFloorParams: boolean,
): IInventorySmartFloorPayloadUI {
  if (controlBidFloor <= 0) {
    controlBidFloor = inventorySmartFloorConfig.FallBackBaseFloor;
    inventorySmartFloor.isUsingFallbackFloor = true;
  }
  inventorySmartFloor.ControlBidFloor = controlBidFloor;
  if (shouldUpdateSmartFloorParams) {
    const params = computeInventorySmartFloorParamsFromConfig(
      inventorySmartFloorConfig,
      controlBidFloor,
    );
    inventorySmartFloor.MaxBidFloor = params.MaxBidFloor;
    inventorySmartFloor.MinBidFloor = params.MinBidFloor;
    inventorySmartFloor.TestIncrement = params.TestIncrement;
    inventorySmartFloor.FallBackBaseFloor = params.FallBackBaseFloor;
  }

  return inventorySmartFloor;
}

export function genNewInventorySmartFloor(
  bidderID: number,
  bidderName: string,
  siteID: number,
  publisherID: number,
  controlBidFloor: number,
  inventorySmartFloorConfig: IInventorySmartFloorConfigUI,
): IInventorySmartFloorPayloadUI {
  const params = computeInventorySmartFloorParamsFromConfig(
    inventorySmartFloorConfig,
    controlBidFloor,
  );

  return {
    Active: true,
    BidderID: bidderID,
    BidderName: bidderName,
    ControlBidFloor: convertNumToFixedDecimalNum(controlBidFloor, 2),
    EstimatedRevenueUplift: 0,
    EstimatedRevenueUplift12HourTrend: 0,
    HeaderbidderID: bidderID,
    ID: 0,
    MaxBidFloor: convertNumToFixedDecimalNum(params.MaxBidFloor, 2),
    MinBidFloor: convertNumToFixedDecimalNum(params.MinBidFloor, 2),
    PublisherID: publisherID,
    RestartVersion: 0,
    SiteID: siteID,
    Status: '',
    TestIncrement: 0.05,
    TestPeriod: 0,
    FallBackBaseFloor: convertNumToFixedDecimalNum(params.FallBackBaseFloor, 2),
    ExcludedHeaderbidderIDs: params.ExcludedHeaderbidderIDs,
  } as IInventorySmartFloorPayloadUI;
}

/** getSmartFloorConfigs
 * retrieves smart floor configs for a specific publisher and bidder
 *
 * @param publisherID - The publisher identifier
 * @param bidderID - The bidder identifier
 * @param preloadSmartFloors - Optional flag to preload associated smart floors data
 * @returns Promise<ISmartFloorConfig[] | null | undefined> - Array of smart floor configurations
 *
 * HTTP Method: GET
 * URL Format: /smart_floor_configs?publisher_id={publisherID}&bidder_id={bidderID}&preload_smart_floors={preloadSmartFloors}
 */
export async function getSmartFloorConfigs(
  publisherID: string,
  bidderID: string,
  preloadSmartFloors: boolean = false,
): Promise<ISmartFloorConfig[] | null | undefined> {
  const reqUrl = `${createUrl(
    '/smart_floor_configs',
    publisherID,
  )}&bidder_id=${bidderID}&preload_smart_floors=${preloadSmartFloors}`;

  try {
    const res = await restGet<ISmartFloorConfig[]>(reqUrl);
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

/** createSmartFloorConfig
 * creates a new smart floor config for a specific publisher and bidder
 */
export async function createSmartFloorConfig(
  publisherID: string,
  bidderID: string,
  smartFloorConfig: ISmartFloorConfig,
): Promise<ISmartFloorConfig | null | undefined> {
  const reqUrl = `${createUrl('/smart_floor_configs', publisherID)}&bidder_id=${bidderID}`;

  try {
    const res = await restPost<ISmartFloorConfig>(reqUrl, smartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty smart floors response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/** updateSmartFloorConfig
 * updates an existing smart floor config for a specific publisher and bidder
 */
export async function updateSmartFloorConfig(
  publisherID: string,
  bidderID: string,
  smartFloorConfig: ISmartFloorConfig,
): Promise<ISmartFloorConfig | null | undefined> {
  const reqUrl = `${createUrl(
    `/smart_floor_configs/${smartFloorConfig.ID}`,
    publisherID,
  )}&bidder_id=${bidderID}`;

  try {
    const res = await restPatch<ISmartFloorConfig>(reqUrl, smartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty smart floors response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}

// Inventory smart floor
export async function getInventorySmartFloorConfigs(
  publisherID: string,
  siteID: string,
  preloadSmartFloors: boolean = false,
): Promise<InventorySmartFloorConfig[] | null | undefined> {
  const reqUrl = `${createUrl(
    '/inventory_smart_floor_configs',
    publisherID,
  )}&site_id=${siteID}&preload_smart_floors=${preloadSmartFloors}`;

  try {
    const res = await restGet<InventorySmartFloorConfig[]>(reqUrl);
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    throw e;
  }
}

/** createSmartFloorConfig
 * creates a new smart floor config for a specific publisher and bidder
 */
export async function createInventorySmartFloorConfig(
  publisherID: string,
  siteID: string,
  smartFloorConfig: InventorySmartFloorConfig,
): Promise<InventorySmartFloorConfig | null | undefined> {
  const reqUrl = `${createUrl('/inventory_smart_floor_configs', publisherID)}&site_id=${siteID}`;

  try {
    const res = await restPost<InventorySmartFloorConfig>(reqUrl, smartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty inventory smart floor config response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}

// Update inventory smart floor config
export async function updateInventorySmartFloorConfig(
  publisherID: string,
  siteID: string,
  inventorySmartFloorConfig: IInventorySmartFloorConfigUI,
): Promise<IInventorySmartFloorConfigUI | null | undefined> {
  const reqUrl = `${createUrl(
    `/inventory_smart_floor_configs/${inventorySmartFloorConfig.ID}`,
    publisherID,
  )}&site_id=${siteID}`;

  try {
    const res = await restPatch<IInventorySmartFloorConfigUI>(reqUrl, inventorySmartFloorConfig);
    if (isEmpty(res) || isEmpty(res.parsedBody)) {
      throw new Error('empty inventory smart floor config response');
    }
    return res.parsedBody;
  } catch (e) {
    console.error(e);
    return null;
  }
}
